/**
 * Role2EnhancedFacility.js
 * PSP/CRE Tool v2.0 - Medical Facility Models
 * 
 * Role 2 Enhanced Facility - Division Medical Support
 * Enhanced surgical capability with ICU support
 * 
 * Capabilities:
 * - All Role 2 Basic capabilities PLUS:
 * - Enhanced surgical capacity (4-6 teams)
 * - ICU capability (10-20 beds)
 * - Dental, mental health support
 * - Extended laboratory
 * - Blood bank with component therapy
 * - Hold time: Up to 7 days
 * 
 * Author: Dr. Tatar Radu MD
 * Institution: Romanian Military Medical Institute
 * Date: December 26, 2024
 * Version: 2.0 - Phase 2 Evacuation Asset Architecture
 * 
 * PHASE 2 CHANGES:
 * - Added organic evacuation assets (30 ambulances)
 * - Added helicopter allocation (4-6 on-call)
 * - Added PULL from Role 2 Basics, PUSH to Role 3
 * - Division-level asset coordination
 */

import { BaseMedicalFacility, EVACUATION_METHODS } from './BaseMedicalFacility.js';

// ============================================================================
// ROLE 2 ENHANCED ORGANIC ASSET STANDARDS
// ============================================================================

/**
 * Default organic assets for Role 2 Enhanced (Division Medical)
 * 
 * Role 2E is the division medical support element with:
 * - 30 ground ambulances (mix of types)
 * - 4-6 helicopters on-call from Division CAB or Air Force
 * - Full medical company personnel
 */
export const ROLE2_ENHANCED_ORGANIC_ASSETS = {
  STANDARD: {
    ground_ambulances: {
      wheeled_light: 15,
      wheeled_armored: 12,
      tracked: 3
    },
    helicopters: {
      allocated: 0,      // Not organic
      on_call: 5         // Division typically allocates 4-6
    },
    personnel: {
      medics: 40,
      drivers: 30,
      litter_bearers: 36,
      flight_medics: 4
    }
  },
  
  HEAVY_DIVISION: {
    // Armored/mechanized division
    ground_ambulances: {
      wheeled_light: 12,
      wheeled_armored: 12,
      tracked: 8         // More tracked for armored ops
    },
    helicopters: {
      allocated: 0,
      on_call: 4
    },
    personnel: {
      medics: 44,
      drivers: 32,
      litter_bearers: 40,
      flight_medics: 2
    }
  },
  
  LIGHT_DIVISION: {
    // Light/airborne/air assault division
    ground_ambulances: {
      wheeled_light: 18,
      wheeled_armored: 8,
      tracked: 0
    },
    helicopters: {
      allocated: 0,
      on_call: 8          // Higher helicopter allocation for light forces
    },
    personnel: {
      medics: 36,
      drivers: 26,
      litter_bearers: 30,
      flight_medics: 8
    }
  }
};

// ============================================================================
// ROLE 2 ENHANCED FACILITY CLASS
// ============================================================================

export class Role2EnhancedFacility extends BaseMedicalFacility {
  /**
   * Create a Role 2 Enhanced facility
   * 
   * @param {Object} config - Facility configuration
   * @param {string} config.facility_id - Unique ID
   * @param {string} config.facility_name - Human-readable name
   * @param {string} config.parent_unit_id - Parent division ID
   * @param {string} [config.division_type='STANDARD'] - Division type
   * @param {Array} [config.supported_role2s=[]] - Role 2 Basics this supports
   * @param {number} [config.position_feba_km=40] - Distance from FEBA
   * @param {number} [config.beds=100] - Number of beds
   * @param {number} [config.surgical_teams=6] - Surgical teams
   * @param {number} [config.icu_beds=20] - ICU beds
   */
  constructor(config) {
    const divisionType = config.division_type || 'STANDARD';
    const organicAssets = config.organic_assets || 
                          ROLE2_ENHANCED_ORGANIC_ASSETS[divisionType] || 
                          ROLE2_ENHANCED_ORGANIC_ASSETS.STANDARD;
    
    const supportedRole2s = config.supported_role2s || [];
    
    super({
      ...config,
      role_level: 2,
      facility_type: 'ROLE2_ENHANCED',
      parent_unit_type: 'DIVISION',
      beds: config.beds || 100,
      surgical_teams: config.surgical_teams || 6,
      icu_beds: config.icu_beds || 20,
      position_feba_km: config.position_feba_km !== undefined ? config.position_feba_km : 40,
      organic_assets: organicAssets,
      
      // Phase 2: Role 2E evacuation config
      evacuation_config: config.evacuation_config || {
        incoming_method: EVACUATION_METHODS.PULL,         // Pulls from Role 2 Basics
        outgoing_method: 'AUTO',         // Mixed - may push or be pulled by Role 3
        picks_up_from: supportedRole2s,
        delivers_to: null,               // Role 3 - set by force structure
        helicopter_priority: 'NORMAL',
        max_hold_time_hours: 168,        // 7 days
        triggers: {
          capacity_threshold: 70,
          time_threshold_hours: 120,
          t1_immediate_evac: true
        }
      }
    });
    
    // Role 2E specific properties
    this.parent_division_id = config.parent_division_id || config.parent_unit_id;
    this.division_type = divisionType;
    this.hold_time_hours = 168;          // 7 days
    this.can_perform_surgery = true;
    this.can_perform_definitive_surgery = true;  // Limited definitive surgery
    this.specialized_care = true;
    
    // Enhanced surgical capacity
    this.surgical_capacity = {
      max_surgeries_per_day: this.surgical_teams * 5,
      surgeries_today: 0,
      surgical_backlog: 0,
      specialized_teams: {
        orthopedic: Math.floor(this.surgical_teams * 0.2),
        general: Math.ceil(this.surgical_teams * 0.8)
      }
    };
    
    // ICU capability
    this.icu_capability = {
      total_icu_beds: this.icu_beds,
      icu_beds_occupied: 0,
      ventilators: Math.floor(this.icu_beds * 0.6),
      ventilators_in_use: 0
    };
    
    // Blood bank (larger than Role 2B)
    this.blood_bank = config.blood_bank || {
      prbc_units: 200,
      ffp_units: 200,
      platelet_units: 100,
      cryoprecipitate_units: 50,
      walking_blood_bank_activated: false,
      massive_transfusion_protocol_available: true
    };
    
    // Supported Role 2 Basics
    this.supported_role2s = supportedRole2s;
  }
  
  // ============================================================================
  // OVERRIDE: GET DEFAULT ORGANIC ASSETS
  // ============================================================================
  
  _getDefaultOrganicAssets() {
    return ROLE2_ENHANCED_ORGANIC_ASSETS[this.division_type] || 
           ROLE2_ENHANCED_ORGANIC_ASSETS.STANDARD;
  }
  
  // ============================================================================
  // PHASE 2: DIVISION-LEVEL ASSET COORDINATION
  // ============================================================================
  
  /**
   * Add Role 2 Basic to support list
   */
  addSupportedRole2(role2FacilityId) {
    if (!this.supported_role2s.includes(role2FacilityId)) {
      this.supported_role2s.push(role2FacilityId);
      this.addPickupSource(role2FacilityId);
    }
  }
  
  /**
   * Get pull capacity distribution across Role 2 Basics
   */
  getPullCapacityByRole2(role2Distances = {}) {
    const effective = this.getEffectiveAssets();
    const totalAmbulances = effective.ground_ambulances.total;
    
    const numRole2s = this.supported_role2s.length || 1;
    const ambulancesPerRole2 = Math.floor(totalAmbulances / numRole2s);
    
    const capacityByRole2 = {};
    
    this.supported_role2s.forEach(role2Id => {
      const distance = role2Distances[role2Id] || 20;
      const tripsPerDay = Math.max(1, 4 * (40 / distance));
      
      capacityByRole2[role2Id] = {
        ambulances_allocated: ambulancesPerRole2,
        distance_km: distance,
        trips_per_day: Math.round(tripsPerDay),
        patients_per_day: Math.round(ambulancesPerRole2 * 4 * tripsPerDay)
      };
    });
    
    return {
      total_ambulances: totalAmbulances,
      supported_role2s: this.supported_role2s.length,
      capacity_by_role2: capacityByRole2,
      total_daily_capacity: Object.values(capacityByRole2)
        .reduce((sum, c) => sum + c.patients_per_day, 0)
    };
  }
  
  /**
   * Allocate helicopters to subordinate facilities
   * Division coordinates helicopter allocation
   * 
   * @param {Object} allocation - Map of facility_id -> helicopter count
   */
  allocateHelicoptersToSubordinates(allocation) {
    const effective = this.getEffectiveAssets();
    const totalAvailable = effective.helicopters.on_call;
    const totalRequested = Object.values(allocation).reduce((sum, n) => sum + n, 0);
    
    if (totalRequested > totalAvailable) {
      return {
        success: false,
        error: 'INSUFFICIENT_HELICOPTERS',
        available: totalAvailable,
        requested: totalRequested
      };
    }
    
    // Track allocation
    const allocationRecords = [];
    for (const [facilityId, count] of Object.entries(allocation)) {
      allocationRecords.push({
        facility_id: facilityId,
        helicopters_allocated: count,
        timestamp: new Date().toISOString()
      });
    }
    
    return {
      success: true,
      allocations: allocationRecords,
      remaining: totalAvailable - totalRequested
    };
  }
  
  // ============================================================================
  // ICU MANAGEMENT
  // ============================================================================
  
  admitToICU(patient, requiresVentilator = false) {
    if (this.icu_capability.icu_beds_occupied >= this.icu_capability.total_icu_beds) {
      return { success: false, reason: 'ICU_FULL', beds_available: 0 };
    }
    
    if (requiresVentilator && 
        this.icu_capability.ventilators_in_use >= this.icu_capability.ventilators) {
      return { success: false, reason: 'NO_VENTILATORS_AVAILABLE', ventilators_available: 0 };
    }
    
    this.icu_capability.icu_beds_occupied++;
    if (requiresVentilator) {
      this.icu_capability.ventilators_in_use++;
    }
    
    return {
      success: true,
      patient_id: patient.id,
      icu_bed_assigned: this.icu_capability.icu_beds_occupied,
      ventilator_assigned: requiresVentilator
    };
  }
  
  dischargeFromICU(patient, wasOnVentilator = false) {
    this.icu_capability.icu_beds_occupied = Math.max(0, this.icu_capability.icu_beds_occupied - 1);
    if (wasOnVentilator) {
      this.icu_capability.ventilators_in_use = Math.max(0, this.icu_capability.ventilators_in_use - 1);
    }
  }
  
  getICUStatus() {
    return {
      icu_beds_total: this.icu_capability.total_icu_beds,
      icu_beds_occupied: this.icu_capability.icu_beds_occupied,
      icu_beds_available: this.icu_capability.total_icu_beds - this.icu_capability.icu_beds_occupied,
      icu_occupancy_percent: ((this.icu_capability.icu_beds_occupied / this.icu_capability.total_icu_beds) * 100).toFixed(1),
      ventilators_total: this.icu_capability.ventilators,
      ventilators_in_use: this.icu_capability.ventilators_in_use,
      ventilators_available: this.icu_capability.ventilators - this.icu_capability.ventilators_in_use
    };
  }
  
  // ============================================================================
  // SURGICAL CAPACITY
  // ============================================================================
  
  canPerformSurgery() {
    return true;
  }
  
  canPerformDefinitiveSurgery() {
    return true;
  }
  
  performDefinitiveSurgery(patient, surgeryType = 'GENERAL') {
    if (this.surgical_capacity.surgeries_today >= this.surgical_capacity.max_surgeries_per_day) {
      return {
        success: false,
        reason: 'SURGICAL_CAPACITY_EXCEEDED',
        backlog: ++this.surgical_capacity.surgical_backlog
      };
    }
    
    this.surgical_capacity.surgeries_today++;
    this.stats.total_surgeries_performed++;
    
    return {
      success: true,
      surgery_type: surgeryType,
      patient_id: patient.id,
      timestamp: new Date(),
      expected_recovery_time_hours: surgeryType === 'ORTHOPEDIC' ? 96 : 72
    };
  }
  
  getSurgicalCapacity() {
    return {
      total_teams: this.surgical_teams,
      max_surgeries_per_day: this.surgical_capacity.max_surgeries_per_day,
      surgeries_today: this.surgical_capacity.surgeries_today,
      surgeries_remaining_today: this.surgical_capacity.max_surgeries_per_day - this.surgical_capacity.surgeries_today,
      surgical_backlog: this.surgical_capacity.surgical_backlog,
      specialized_teams: this.surgical_capacity.specialized_teams,
      can_perform_definitive_surgery: this.can_perform_definitive_surgery
    };
  }
  
  // ============================================================================
  // BLOOD BANK
  // ============================================================================
  
  getBloodBankStatus(daily_consumption = 40) {
    const days_supply = daily_consumption > 0 ? this.blood_bank.prbc_units / daily_consumption : 999;
    
    let alert_level = 'GREEN';
    if (days_supply < 0.5) alert_level = 'BLACK';
    else if (days_supply < 1) alert_level = 'RED';
    else if (days_supply < 3) alert_level = 'YELLOW';
    
    return {
      prbc_units: this.blood_bank.prbc_units,
      ffp_units: this.blood_bank.ffp_units,
      platelet_units: this.blood_bank.platelet_units,
      cryoprecipitate_units: this.blood_bank.cryoprecipitate_units,
      days_supply: days_supply,
      alert_level: alert_level,
      critical: alert_level === 'BLACK' || alert_level === 'RED',
      mtp_available: this.blood_bank.massive_transfusion_protocol_available
    };
  }
  
  resupplyBlood(units) {
    if (units.prbc_units) this.blood_bank.prbc_units += units.prbc_units;
    if (units.ffp_units) this.blood_bank.ffp_units += units.ffp_units;
    if (units.platelet_units) this.blood_bank.platelet_units += units.platelet_units;
    if (units.cryoprecipitate_units) this.blood_bank.cryoprecipitate_units += units.cryoprecipitate_units;
  }
  
  // ============================================================================
  // OVERRIDE METHODS
  // ============================================================================
  
  getOperationalIssues() {
    const issues = super.getOperationalIssues();
    
    if (this.surgical_capacity.backlog > 0) {
      issues.push(`Surgical backlog: ${this.surgical_capacity.backlog} patients`);
    }
    
    const blood_status = this.getBloodBankStatus();
    if (blood_status.critical) {
      issues.push(`Critical blood shortage (${this.blood_bank.prbc_units} units)`);
    }
    
    const icu_status = this.getICUStatus();
    if (parseFloat(icu_status.icu_occupancy_percent) > 90) {
      issues.push(`ICU near capacity (${icu_status.icu_occupancy_percent}%)`);
    }
    
    if (this.supported_role2s.length === 0) {
      issues.push('No Role 2 Basics configured for support');
    }
    
    return issues;
  }
  
  toJSON() {
    return {
      ...super.toJSON(),
      parent_division_id: this.parent_division_id,
      division_type: this.division_type,
      hold_time_hours: this.hold_time_hours,
      can_perform_surgery: this.can_perform_surgery,
      can_perform_definitive_surgery: this.can_perform_definitive_surgery,
      specialized_care: this.specialized_care,
      surgical_capacity: this.getSurgicalCapacity(),
      icu_status: this.getICUStatus(),
      blood_bank_status: this.getBloodBankStatus(),
      supported_role2s: this.supported_role2s,
      pull_capacity: this.getPullCapacityByRole2()
    };
  }
}

// ============================================================================
// EXPORTS
// ============================================================================

// Already exported at definition

export default Role2EnhancedFacility;
